import classNames from 'classnames'
import { push } from 'connected-react-router'
import { db, FirebaseTimestamp } from 'firebase/index'
import { State } from 'index'
import React, { FC, useState, ChangeEvent, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import form from 'styles/components/form.module.scss'
import styles from 'styles/components/template.module.scss'
import text from 'styles/utility/text.module.scss'
import { Description, MainHeadline, SubDescription } from 'components/Typography'
import { Spacer } from 'components/Utility'
import { FormLabel } from 'components/Label'
import {
  SelectBox,
  TextInput,
  Textarea,
  PointCardExpirationForm,
  Checkbox,
  PointCardGradeForm,
  PointCardColorForm,
  ImageCrop,
} from 'components/Form'
import { BlueButton, GreyButton } from 'components/Button'
import { AlertCard, PointCardPreviewCard } from 'components/Card'
import { fromLastAcquiredPointDateOptions } from 'constants/pointCard'
import { useFetchBrandName } from 'hooks/brand'
import { useString, useSelect, useSelectOptionsAmount, useImageUrl } from 'hooks/form'
import { usePointCardExpiration, usePointCardGrades } from 'hooks/pointCard'
import { updateAuthBrandRef } from 'reducks/auth/operations'
import { getBrandRef, getIsAdmin, getStoreRef } from 'reducks/auth/selectors'
import { hideLoading, showLoading } from 'reducks/loading/operations'
import { fetchPointCard, setPointCard } from 'repos/pointCard'
import { setBrand } from 'repos/brand'
import { PointCardActionType } from 'types/pointCard'
import { fetchStoreStaffs, postStaffApi, setStaff, toStaffApiErrorMessage } from 'repos/staff'
import { CREATE_OWN_BRAND_ENDPOINT } from 'constants/urls'

type Props = {
  isEdit: boolean
  pointCardId?: string
}

const PointCardEditTemplate: FC<Props> = (props) => {
  const { isEdit, pointCardId = db.collection('point_cards').doc().id } = props

  const dispatch = useDispatch()
  const brandRef = useSelector((state: State) => getBrandRef(state))
  const storeRef = useSelector((state: State) => getStoreRef(state))
  const isAdmin = useSelector((state: State) => getIsAdmin(state))

  const brandName = useFetchBrandName()

  const { imageUrl: iconUrl, setImageUrl: setIconUrl, handleImageUrl: handleIconUrl } = useImageUrl()
  const { state: name, handleState: handleName, setState: setName } = useString<string>('')
  const {
    expiration,
    expirationType,
    handleExpirationType,
    handleExpiresAt,
    handleExpirationPeriod,
    setExpiration,
    setExpirationType,
  } = usePointCardExpiration()
  const [actions, setActions] = useState<PointCardActionType[]>(['sacri'])
  const handleActions = (e: ChangeEvent<HTMLInputElement>) => {
    setActions((prevState) => {
      const id = e.target.id as PointCardActionType
      return prevState.includes(id) ? prevState.filter((action) => action !== id) : [...prevState, id]
    })
  }
  const { state: amountUnit, handleState: handleAmountUnit, setState: setAmountUnit } = useSelect<string>('100')
  const { state: amountPoints, handleState: handleAmountPoints, setState: setAmountPoints } = useSelect<string>('1')
  const { grades, setGrades, ...pointCardGradesRest } = usePointCardGrades()
  const { state: initialPoints, handleState: handleInitialPoints, setState: setInitialPoints } = useSelect<string>('0')
  const { state: color, handleState: handleColor, setState: setColor } = useString<string>('')
  const { state: note, handleState: handleNote, setState: setNote } = useString<string>('')

  const amountUnitOptions = useSelectOptionsAmount({ total: 50, unit: 100, unitString: '円' })
  const amountPointsOptions = useSelectOptionsAmount({ total: 20, unit: 1, unitString: 'pt' })
  const initialPointsOptions = useSelectOptionsAmount({ total: 20, unit: 1, startIndex: 0, unitString: 'pt' })

  const handleCancel = () => dispatch(push('/point_card'))

  const handleSubmit = async () => {
    const now = FirebaseTimestamp.now()
    dispatch(showLoading())
    try {
      let brand_ref = brandRef
      // ブランド未作成の場合はブランドを新規作成する
      if (!brand_ref) {
        // スーパー管理者がポイントカードを作成してブランドが作成されたら、店舗管理者をブランドマネージャーとする
        if (isAdmin) {
          brand_ref = db.collection('brands').doc()
          await setBrand(brand_ref, {
            name, // ポイントカードと同じ名前で作成する
            store_refs: [storeRef],
            created_at: now,
            updated_at: now,
          })
          const staffs = await fetchStoreStaffs(storeRef)
          await Promise.all(
            staffs.map(async (staff) => {
              staff.roles = staff.roles.map((role) =>
                role.store_ref.id === storeRef.id ? { ...role, brand_ref } : role
              )
              const staffRef = db.collection('staffs').doc(staff.uid)
              await setStaff(staffRef, staff)
            })
          )
        } else {
          // 店舗管理者は自身の権限を直接書き換えられないため、ブランドの作成と自身のブランドマネージャーの設定を
          // Functions で 1 つのトランザクションとして行う（失敗してもブランドだけが残ることはない）
          const { brand_id } = await postStaffApi<{ brand_id: string }>(CREATE_OWN_BRAND_ENDPOINT, {
            store_id: storeRef.id,
            name, // ポイントカードと同じ名前で作成する
          })
          brand_ref = db.collection('brands').doc(brand_id)
        }
        await dispatch(updateAuthBrandRef(brand_ref))
      }
      const prevPointCard = isEdit ? await fetchPointCard(pointCardId) : null
      await setPointCard({
        ref: db.collection('point_cards').doc(pointCardId),
        brand_ref,
        name,
        icon_url: iconUrl,
        condition: {
          amount_unit: Number(amountUnit),
          amount_points: Number(amountPoints),
          initial_points: Number(initialPoints),
        },
        color,
        grades,
        note,
        expiration,
        actions,
        status: prevPointCard ? prevPointCard.status : 'suspended',
        created_at: prevPointCard ? prevPointCard.created_at : now,
        updated_at: now,
      })
      dispatch(hideLoading())
      dispatch(push('/point_card/complete'))
    } catch (e) {
      dispatch(hideLoading())
      if (e instanceof Error) {
        console.error(e.message)
      }
      alert(toStaffApiErrorMessage(e, 'ポイントカードの保存に失敗しました。'))
    }
  }

  const isDisabledSubmit =
    !name ||
    !iconUrl ||
    !expirationType ||
    !amountUnit ||
    !amountPoints ||
    !initialPoints ||
    !color ||
    grades.length < 3 ||
    grades.length > 6 ||
    grades.some((grade) => grade.name === '')

  useEffect(() => {
    ;(async () => {
      if (!isEdit) return
      dispatch(showLoading())
      const pointCard = await fetchPointCard(pointCardId)
      dispatch(hideLoading())
      if (!pointCard) return
      setIconUrl(pointCard.icon_url)
      setName(pointCard.name)
      setAmountUnit(pointCard.condition.amount_unit.toString())
      setAmountPoints(pointCard.condition.amount_points.toString())
      setInitialPoints(pointCard.condition.initial_points.toString())
      setColor(pointCard.color)
      setGrades(pointCard.grades)
      setNote(pointCard.note)
      setActions(pointCard.actions)
      // 有効期限
      setExpiration(pointCard.expiration)
      if (pointCard.expiration?.expires_at) {
        setExpirationType('specified_date')
      } else if (pointCard.expiration?.expiration_period) {
        setExpirationType('from_last_acquired_point_date')
      } else {
        setExpirationType('no_limit')
      }
    })()
  }, [isEdit, pointCardId])

  return (
    <div className={styles.standard}>
      <Spacer size={40} />
      <MainHeadline headline={isEdit ? 'ポイントカードを編集する' : 'ポイントカードを作成する'} />
      <Spacer size={20} />
      <Description description={'必要な情報を登録してください。\n作成後「公開する」までは公開されません。'} />
      {!isEdit && brandName && (
        <>
          <Spacer size={20} />
          <AlertCard
            headline={'ブランドでポイントカードを作成します'}
            title={'注意'}
            description={`この店舗は、${brandName}に紐づいているため店舗のポイントカードは作成できません。\n個別に作成する場合は、運営者にブランドの紐づけを解除してもらってください。`}
          />
        </>
      )}
      <Spacer size={20} />
      <FormLabel label={'ポイントカード名称'} required={true} />
      <Spacer size={8} />
      <SubDescription
        description={
          'ポイントカード券面のタイトルに表示される名称です。店舗名や店舗ブランド名を記載してください。'
        }
      />
      <Spacer size={8} />
      <TextInput
        disabled={false}
        required={false}
        onChange={handleName}
        placeholder={'sacri Group'}
        size={'small'}
        type={'text'}
        value={name}
      />
      <Spacer size={20} />
      <FormLabel label={'アイコン画像'} required={true} />
      <Spacer size={8} />
      <ImageCrop
        folderPath={`point_cards/${pointCardId}`}
        handleImageUrl={handleIconUrl}
        imageSize={{ width: 64, height: 64 }}
        imageUrl={iconUrl}
      />
      <Spacer size={20} />
      <FormLabel label={'有効期限'} required={true} />
      <Spacer size={4} />
      <SubDescription description={'カードの有効期限を下記のいづれかから選択してください'} />
      <Spacer size={8} />
      <PointCardExpirationForm
        expiration={expiration}
        expirationType={expirationType}
        fromLastAcquiredPointDateOptions={fromLastAcquiredPointDateOptions}
        handleExpirationPeriod={handleExpirationPeriod}
        handleExpirationType={handleExpirationType}
        handleExpiresAt={handleExpiresAt}
      />
      <Spacer size={20} />
      <FormLabel label={'sacri購入時のポイント付与条件'} required={true} />
      <Spacer size={4} />
      <SubDescription description={'決済金額（税込）ごとに何ポイント付与するかを設定してください。'} />
      <Spacer size={8} />
      <div className={form.column_two}>
        <div className={classNames(form.column_two__item, text.font_small)}>金額</div>
        <Spacer size={8} />
        <div className={classNames(form.column_two__item, text.font_small)}>付与ポイント数</div>
      </div>
      <Spacer size={8} />
      <div className={form.column_two}>
        <div className={classNames(form.column_two__item)}>
          <SelectBox options={amountUnitOptions} select={handleAmountUnit} value={amountUnit} />
        </div>
        <Spacer size={8} />
        <div className={classNames(form.column_two__item)}>
          <SelectBox options={amountPointsOptions} select={handleAmountPoints} value={amountPoints} />
        </div>
      </div>
      <Spacer size={20} />
      <FormLabel label={'グレード'} required={true} />
      <Spacer size={4} />
      <SubDescription
        description={'獲得ポイントごとに、グレード名を設定してください。\nグレードは、3つから6つまで設定できます。'}
      />
      <Spacer size={8} />
      <PointCardGradeForm grades={grades} {...pointCardGradesRest} />
      <Spacer size={20} />
      <FormLabel label={'初回ポイントカード発行時付与ポイント'} required={false} />
      <Spacer size={8} />
      <SubDescription
        description={
          '初回ポイントカードを発行した際に、初期ポイントを付与する場合は設定してください。0Ptからスタートする場合は0Ptのままにしてください。'
        }
      />
      <Spacer size={20} />
      <SelectBox options={initialPointsOptions} select={handleInitialPoints} value={initialPoints} />
      <Spacer size={20} />
      <FormLabel label={'ベースカラー'} required={true} />
      <Spacer size={4} />
      <SubDescription description={'カードの色を選択してください'} />
      <Spacer size={8} />
      <PointCardColorForm color={color} handleColor={handleColor} />
      <Spacer size={20} />
      <FormLabel label={'プレビュー'} required={false} />
      <Spacer size={20} />
      <PointCardPreviewCard
        pointCard={{
          color,
          expiration,
          grades,
          icon_url: iconUrl,
          name,
        }}
      />
      <Spacer size={20} />
      <MainHeadline headline={'注意事項の作成'} />
      <Spacer size={20} />
      <SubDescription
        description={'アプリ上で、お客さまに対してポイントカードシステムに関して説明するために必要な項目です。'}
      />
      <Spacer size={20} />
      <FormLabel label={'店頭でのポイント付与の表記'} required={false} />
      <Spacer size={8} />
      <SubDescription
        description={'店頭（レジ）での決済に応じて、QRコードでポイント付与する場合はチェックを入れてください。'}
      />
      <Spacer size={20} />
      <Checkbox
        id={'store'}
        isChecked={actions.includes('store')}
        label={'店頭（レジ）での決済'}
        onChange={handleActions}
        size={'large'}
      />
      <Spacer size={20} />
      <FormLabel label={'注意事項'} required={false} />
      <Spacer size={8} />
      <Textarea disabled={false} maxLength={1000} onChange={handleNote} required={false} rows={3} value={note} />
      <Spacer size={40} />
      <BlueButton disabled={isDisabledSubmit} label={'登録する'} onClick={handleSubmit} size={'medium'} type={'main'} />
      <Spacer size={24} />
      <GreyButton label={'ポイントカードへ戻る'} onClick={handleCancel} size={'medium'} type={'outlined'} />
      <Spacer size={40} />
    </div>
  )
}

export default PointCardEditTemplate
